import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, map, of, startWith, switchMap, take } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { MarkdownService } from '../../../services/markdown.service';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import {
  Release,
  ReleaseNoteItem,
  ReleaseNoteType,
} from '../../../models/release-note';
import { RELEASES } from './release-notes';
import { ReleaseNotesService } from './release-notes.service';

/** Rendering state of the technical changelog; `null` while loading. */
type ChangelogState = { html: string } | { error: true } | null;

type ChangelogMode = 'news' | 'technical';

interface ReleaseView extends Release {
  dateLabel: string | null;
  highlights: ReleaseNoteItem[];
  groups: { type: ReleaseNoteType; items: ReleaseNoteItem[] }[];
}

export const RELEASE_NOTE_TYPES: Record<
  ReleaseNoteType,
  { label: string; icon: string }
> = {
  new: { label: 'Neu', icon: 'fas fa-star' },
  improved: { label: 'Verbessert', icon: 'fas fa-arrow-trend-up' },
  fixed: { label: 'Behoben', icon: 'fas fa-screwdriver-wrench' },
};

const TYPE_ORDER: ReleaseNoteType[] = ['new', 'improved', 'fixed'];

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long' });

/**
 * "Neuigkeiten" overlay: user-facing release notes (see release-notes.ts),
 * with administrator topics only for administrators. Super-Admins can switch
 * to the technical CHANGELOG.md, which is copied into `assets/` at build time
 * (see angular.json) and therefore matches the deployed version.
 */
@Component({
  selector: 'app-changelog',
  standalone: true,
  imports: [
    MessageModule,
    SkeletonModule,
    SelectButtonModule,
    FormsModule,
    NgTemplateOutlet,
  ],
  templateUrl: './changelog.component.html',
  styleUrl: './changelog.component.scss',
})
export class ChangelogComponent implements OnInit, AfterViewInit {
  private readonly http = inject(HttpClient);
  private readonly markdownService = inject(MarkdownService);
  private readonly sidebarService = inject(SidebarService);
  private readonly releaseNotesService = inject(ReleaseNotesService);
  readonly activeUserStore = inject(ActiveUserStore);

  readonly types = RELEASE_NOTE_TYPES;

  private readonly sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-8',
    icon: 'fas fa-bullhorn',
    headerText: 'Neuigkeiten',
    subtitle: 'Was sich im Kommunal-Atlas geändert hat',
  };

  readonly modeOptions: { label: string; value: ChangelogMode; icon: string }[] =
    [
      { label: 'Neuigkeiten', value: 'news', icon: 'fas fa-bullhorn' },
      {
        label: 'Technischer Changelog',
        value: 'technical',
        icon: 'fas fa-code',
      },
    ];

  readonly mode = signal<ChangelogMode>('news');

  /** Administrators also see configuration, user and portal topics. */
  readonly isAdmin = computed(() => {
    const user = this.activeUserStore.user();
    return (
      !!user && (user.isAdmin || user.isSuperAdmin || user.level >= 30)
    );
  });

  readonly releases = computed<ReleaseView[]>(() => {
    const isAdmin = this.isAdmin();
    return RELEASES.map(release => {
      const items = release.items.filter(
        item => item.audience === 'all' || isAdmin
      );
      const highlights = items.filter(item => item.highlight);
      const rest = items.filter(item => !item.highlight);
      return {
        ...release,
        // the title announces the highlights - without visible ones (e.g.
        // admin-only features) it would promise something the user can't see
        title: highlights.length
          ? release.title
          : 'Verbesserungen und Korrekturen',
        items,
        dateLabel: release.date
          ? dateFormat.format(new Date(release.date))
          : null,
        highlights,
        groups: TYPE_ORDER.map(type => ({
          type,
          items: rest.filter(item => item.type === type),
        })).filter(group => group.items.length),
      };
    }).filter(release => release.items.length);
  });

  /** Loaded on first switch to the technical view only. */
  readonly changelog = toSignal<ChangelogState>(
    toObservable(this.mode).pipe(
      filter(mode => mode === 'technical'),
      take(1),
      switchMap(() =>
        this.http.get('/assets/CHANGELOG.md', { responseType: 'text' }).pipe(
          map(markdown => ({
            html: this.markdownService.convertToHtml(markdown) as string,
          })),
          catchError(() => of({ error: true as const })),
          startWith(null)
        )
      )
    ),
    { initialValue: null }
  );

  ngOnInit(): void {
    this.releaseNotesService.markSeen();
  }

  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
  }

  isError(state: ChangelogState): state is { error: true } {
    return !!state && 'error' in state;
  }
}
